import { useState } from 'react';
import styled from 'styled-components';
import { HowWorks } from 'components/HowWorks';
import Link from 'next/link';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { mediaMdMax, mediaMdMin, mediaLgMax, mediaSmMax } from 'helpers/breakpoints';
import Head from 'next/head';
import getConfig from 'next/config';
import Button from 'components/ui/Button/Button';
import boatsetterPaths from 'utils/boatsetterPaths';
import ChevronIcon from 'components/icons/ChevronIcon';
import AppStore from 'components/icons/social/AppStore';
import GooglePlay from 'components/icons/social/GooglePlay';
import BuoyLogo from 'components/dashboard/renterInsurance/components/BuoyLogo';
import SpotlightSection from 'components/homepage/SpotlightSection';

const { RAILS_URL } = getConfig().publicRuntimeConfig;
// The leading slash is intentionally left off, in line with the S3 format. Adding a leading slash will break it.
const s3Paths = {
  captainSpotlight: 'spa/homepage/captain-spotlight-250923.webp',
  managerSpotlight: 'spa/why-list/manager-spotlight-desktop-250923.jpg',
};

const questions = [
  {
    question: 'Is listing free?',
    answer:
      'Yes! We’re in this together and we want you to succeed, so listing is free and we only take a commission off of completed rentals—meaning, we only make money when you do.',
  },
  {
    question: 'Does Boatsetter screen renters?',
    answer:
      'Yes! We verify all of our renters through a service called Cognito, designed to make sure payment information matches a renter’s identity. For your safety (and your boat’s safety), we also have an age policy in place determining that a renter must be at least 18 years old to rent out a captained charter and at least 25 years old to rent out a bareboat charter.',
  },
  {
    question: 'What makes Boatsetter different from other boat rental platforms?',
    answer:
      'We are the only boat rental marketplace in the US that provides peer-to-peer boat rental insurance. The only one. Meaning, no other boat rental platform offers you, as a boat owner, the peace of mind you need to embark on this kind of venture. We’ve got you covered. ',
  },
  {
    question: 'I don’t know if I’m comfortable having someone else drive my boat. How can Boatsetter help?',
    answer:
      'We are the only marketplace that offers a network of USCG-certified captains. We want to set your mind at ease; when you rent out your boat with one of our certified captains, you can rest assured that an experienced professional will be in charge.',
  },
  {
    question: 'How does the insurance work? Do I have to have my own?',
    answer:
      'Under USCG rules, you are required to have some kind of rental policy covering your boat during rentals. You can either choose to provide your own, or use the peer-to-peer policy we provide through Buoy. Please note you must have a personal recreational policy in order to be approved for our program.',
  },
  {
    question: 'How does payment work?',
    answer:
      'We handle all payments associated with the booking. Prior to the trip, we collect the total cost of the booking from the renter, in addition to holding a security deposit on your behalf. Once the trip is completed, you are paid out via direct deposit within 3 days.',
  },
];

const howWorks = {
  Owners: [
    {
      title: 'List',
      text: 'Listing your boat is free and takes less than 15 minutes.',
      icon: '/images/why-list/how/search.svg',
    },
    {
      title: 'Connect',
      text: 'Review booking requests and inquiries from renters.',
      icon: '/images/why-list/how/connect.svg',
    },
    {
      title: 'Manage',
      text: 'Handle all your bookings on the web or in our dedicated Owner app.',
      icon: '/images/why-list/how/book.svg',
    },
    {
      title: 'Collect',
      text: 'Payouts are processed quickly and go directly into your bank account.',
      icon: '/images/why-list/how/cruise.svg',
    },
  ],
};

const carouselItems = [
  {
    text: 'Thanks to Boatsetter, I earned $4,524 in one month and dramatically reduced my cost of ownership!',
    name: 'Mike V.',
    image: '/images/why-list/reviews/user3.jpg',
  },
  {
    text: 'Boatsetter introduced me to qualified captains that gave me peace of mind knowing that my boat is in the hands of a professional.',
    name: 'Juan Pablo D.',
    image: '/images/why-list/reviews/user2.jpg',
  },
  {
    text: 'Unlike boat clubs or standard offline rental programs, Boatsetter gives me complete control to decide everything from availability to pricing.',
    name: 'Bob B.',
    image: '/images/why-list/reviews/user1.jpg',
  },
];

const WhyListPage = () => {
  const [active, setActive] = useState();

  const setQuestionActive = (question) => {
    if (active === question) setActive(undefined);
    else setActive(question);
  };

  const canonicalTag = `${RAILS_URL}/why-list`;

  return (
    <DefaultLayout white noFollow={false}>
      <WhyListPageWrapper>
        <Head>
          <link rel="canonical" href={canonicalTag} key="canonical" />
        </Head>
        <div className="heading">
          <img src="/images/why-list/waves.svg" className="waves" alt="Waves" />
          <div className="cap">
            <div className="container">
              <h1>Earn an average of $20K renting out your boat</h1>
              <p>List for free. Rent when you want.</p>
              <Link href={`${boatsetterPaths.listBoat}?openModal=true`} legacyBehavior>
                <Button variant="yellow" text="Get started" />
              </Link>
            </div>
          </div>
          <picture>
            <source srcSet="/images/why-list/Hero1.webp" type="image/webp" />
            <img src="/images/why-list/Hero1.jpg" className="image" alt="Boating with Boatsetter" />
          </picture>
        </div>

        <div className="insurance">
          <div className="cap">
            <div className="title">
              <h3>
                The only insured <br /> peer-to-peer boat rental marketplace in the US.
              </h3>
              <div className="partner">
                <small>in partnership with</small>
                <BuoyLogo />
              </div>
            </div>
            <div className="text">
              <p>
                Our goal is to ensure all boats owners can safely and easily rent out their boats, so we partnered with
                Buoy to create an exclusive Peer-to-Peer Boat Rental Policy that gives boat owners the confidence to
                participate in our platform.
              </p>
              <a target="_blank" href="https://www.buoy.rent/national-coverage" rel="noreferrer">
                Learn more
              </a>
            </div>
          </div>
        </div>
        <HowWorks heading={<>How does it work?</>} sections={['Owners']} data={howWorks} />
        <div className="steps">
          <div className="cap">
            <h3>Four steps to Boatsetter success</h3>
            <div className="step">
              <div className="text">
                <div className="stepHeading">List your boat</div>
                <p className="stepText">
                  Describe your boat in all its glory and add photos showcasing its best features. Then determine
                  pricing, choose who will operate your boat, and set availability. You&apos;ll be ready to go in no
                  time!
                </p>
              </div>
              <div className="images step1">
                <img loading="lazy" src="/images/why-list/steps/step1-image.jpg" className="picture" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step1-ui.png" className="ui" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step1-svg.svg" className="svg" alt="" />
              </div>
            </div>
            <div className="step">
              <div className="text">
                <div className="stepHeading">Connect with potential renters</div>
                <p className="stepText">
                  We make it easy for you to safely and securely communicate with renters. Once you get a feel for each
                  party, you can approve or decline as you see fit — meaning, you control who goes on your boat.
                </p>
              </div>
              <div className="images step2">
                <img loading="lazy" src="/images/why-list/steps/step2-image.jpg" className="picture" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step2-ui.png" className="ui" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step2-svg.svg" className="svg" alt="" />
              </div>
            </div>
            <div className="step">
              <div className="text">
                <div className="stepHeading">Manage your boat&apos;s schedule</div>
                <p className="stepText">
                  We know life gets busy, so you have control over your boat&apos;s availability. Use the calendar tool
                  to mark certain days or hours as unavailable if something comes up.
                </p>
              </div>
              <div className="images step3">
                <img loading="lazy" src="/images/why-list/steps/step3-image.jpg" className="picture" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step3-ui.png" className="ui" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step3-svg.svg" className="svg" alt="" />
              </div>
            </div>
            <div className="step">
              <div className="text">
                <div className="stepHeading">Collect your earnings</div>
                <p className="stepText">
                  And here’s the fun part: you will be paid securely via direct deposit within three days after each
                  completed booking. No invoicing, no bill collecting, no hassle.
                </p>
              </div>
              <div className="images step4">
                <img loading="lazy" src="/images/why-list/steps/step4-image.jpg" className="picture" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step4-ui.png" className="ui" alt="" />
                <img loading="lazy" src="/images/why-list/steps/step4-svg.svg" className="svg" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div className="reviews">
          <div className="cap">
            <div className="top">
              <h2>What other owners are saying</h2>
            </div>
            <ReviewsScroll>
              {carouselItems.map((item) => (
                <ReviewSlide key={item.name}>
                  <div className="carouselItem">
                    <img loading="lazy" src={item.image} className="profile" alt="" width={80} height={80} />
                    <p>{item.text}</p>
                    <div>
                      <b>{item.name}</b>
                      <div>BOAT OWNER</div>
                    </div>
                  </div>
                </ReviewSlide>
              ))}
            </ReviewsScroll>
          </div>
        </div>

        <SpotlightSection id="captain-feature-spotlight" textSide="right" image={s3Paths.captainSpotlight}>
          <NeedACaptainContent />
        </SpotlightSection>

        <div className="mobileApp">
          <img loading="lazy" className="svg" src="/images/why-list/mobile-apps/mobile-svg.svg" alt="" />
          <div className="cap">
            <div className="text">
              <h3>Manage your boat on the go</h3>
              <p>
                Use the Boatsetter Owner app to manage inquiries and bookings, easily communicate with renters, edit
                your boat listings, and more.
              </p>
              <div className="download">
                <a
                  target="_blank"
                  href="https://apps.apple.com/us/app/boatsetter-owner-app/id1369215908"
                  rel="noreferrer"
                >
                  <AppStore />
                </a>
                <a
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.boatsetter.app&hl=en_US&gl=US"
                  rel="noreferrer"
                >
                  <GooglePlay height={49} width={148} />
                </a>
              </div>
            </div>
            <picture className="pictureWrapper">
              <source srcSet="/images/why-list/mobile-apps/phone.webp" type="image/webp" />
              <img loading="lazy" className="phoneImage" src="/images/why-list/mobile-apps/phone.png" alt="" />
            </picture>
          </div>
        </div>

        <SpotlightSection id="boat-manager-feature-spotlight" textSide="left" image={s3Paths.managerSpotlight}>
          <NeedAHandContent />
        </SpotlightSection>

        <div className="commercial cap">
          <picture>
            <source srcSet="/images/why-list/Commercial.webp" type="image/webp" />
            <img loading="lazy" src="/images/why-list/Commercial.jpg" alt="" />
          </picture>
          <div className="text">
            <h3>Are you a commercial operator?</h3>
            <p>
              Join hundreds of boat rental companies, yacht charter agents, fishing captains, and boat clubs across the
              U.S. receiving leads from Boatsetter, regardless of being an established operator or starting your boat
              rental business.
            </p>
            <div className="links">
              <Link href={`${boatsetterPaths.listBoat}?openModal=true`}>List your fleet</Link> or{' '}
              <a href="https://boatsetter.kustomer.help/contact/contact-us-rybdP8n5i">contact us</a> for assistance
            </div>
          </div>
        </div>

        <div className="faq">
          <div className="cap">
            <h3>Frequently asked questions</h3>
            <div className="container">
              <div className="halfContainer">
                {questions.slice(0, 3).map((question, i) => (
                  <div
                    key={question.question}
                    className={['questionContainer', active === question.question && 'active'].join(' ')}
                  >
                    <div
                      onClick={() => setQuestionActive(question.question)}
                      className="question"
                      role="button"
                      tabIndex={i}
                    >
                      <div className="questionHeading">{question.question}</div>
                      <ChevronIcon
                        className="arrow"
                        height="28px"
                        width="28px"
                        transform={active === question.question ? 'rotate(180)' : null}
                        style={{ minWidth: '28px' }}
                      />
                    </div>
                    <div className="answear">{question.answer}</div>
                  </div>
                ))}
              </div>
              <div className="halfContainer">
                {questions.slice(3, 6).map((question, i) => (
                  <div
                    key={question.question}
                    className={['questionContainer', active === question.question && 'active'].join(' ')}
                  >
                    <div
                      onClick={() => setQuestionActive(question.question)}
                      className="question"
                      role="button"
                      tabIndex={i}
                    >
                      <div className="questionHeading">{question.question}</div>
                      <ChevronIcon
                        className="arrow"
                        height="28px"
                        width="28px"
                        transform={active === question.question ? 'rotate(180)' : null}
                        style={{ minWidth: '28px' }}
                      />
                    </div>
                    <div className="answear">{question.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="stickyBar">
          <div className="cap">
            <div>Earn $20,000 per year or more</div>
            <Link href={`${boatsetterPaths.listBoat}?openModal=true`} passHref legacyBehavior>
              <Button variant="yellow" text="list your boat" />
            </Link>
          </div>
        </div>
      </WhyListPageWrapper>
    </DefaultLayout>
  );
};

export default WhyListPage;

const WhyListPageWrapper = styled.div`
  padding-top: 60px;

  .reviews {
    padding-top: 150px;
    @media ${mediaMdMax} {
      padding-top: 70px;
    }

    .carouselItem {
      background: #ffffff;
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
      border-radius: 10px;
      padding: 25px;
      display: flex;
      flex-flow: column;
      align-items: center;

      p {
        max-width: 280px;
        text-align: center;
        margin-top: 25px;
        font-style: italic;
        min-height: 90px;
        @media ${mediaSmMax} {
          min-height: 110px;
        }
      }

      div {
        text-align: center;

        div {
          color: gray;
          font-size: 12px;
        }
      }

      .profile {
        border: 2px solid #ffffff;
        box-sizing: border-box;
        box-shadow: 0px 4px 20px 2px rgba(0, 0, 0, 0.1);
        border-radius: 50%;
      }
    }

    .top {
      display: flex;

      h2 {
        font-weight: bold;
        font-size: 32px;
        line-height: 44px;
        color: #17233c;
        @media ${mediaLgMax} {
          font-size: 24px;
          line-height: 44px;
        }
        @media ${mediaMdMax} {
          max-width: 300px;
          font-size: 24px;
          line-height: 30px;
        }
      }
    }
  }

  // My IDE is falsely reporting this doesn't exist.
  .howWorks {
    margin-top: 50px !important;
    margin-bottom: 100px !important;

    h2 {
      font-weight: bold;
      font-size: 31px;
      color: #f4c064 !important;
      @media ${mediaMdMax} {
        font-size: 24px;
      }
    }

    @media ${mediaMdMax} {
      margin-bottom: 176px !important;
      margin-top: 70px !important;
      &.cap {
        padding-left: 0 !important;
      }
    }

    @media ${mediaSmMax} {
      margin-bottom: 360px !important;
    }

    .menu {
      background: #0e7873;
      .links {
        visibility: hidden;
        min-width: 200px;
      }
    }

    .info {
      @media ${mediaMdMax} {
        top: calc(100% - 190px);
        padding: 30px 60px 30px 30px;
      }
    }

    &.cap {
      @media ${mediaSmMax} {
        padding: 0;
      }
    }

    .links div.active {
      border-bottom: 2px solid #f4c064;
    }
  }

  .commercial {
    display: flex;
    padding-top: 150px !important;
    @media ${mediaSmMax} {
      flex-flow: column;
    }

    @media ${mediaSmMax} {
      padding-bottom: unset !important;
      border-bottom: unset;
    }

    border-bottom: 1px solid #dbdfe5;
    padding-bottom: 150px !important;

    @media ${mediaMdMax} {
      padding-top: 30px !important;
    }

    &.cap {
      // Remove if we rollback the spotlight section changes
      margin-top: -100px;
      @media ${mediaLgMax} {
        width: unset;
        margin: 0 60px;
        padding: unset;
      }

      @media ${mediaSmMax} {
        margin: 0;
        padding: 0;
      }
    }

    img {
      border-radius: 16px;
      width: 480px;
      @media ${mediaLgMax} {
        width: 300px;
      }
      @media ${mediaSmMax} {
        width: 100%;
        border-radius: 0px;
        height: 250px;
      }
    }

    .text {
      margin: auto 0 auto 80px;
      @media ${mediaLgMax} {
        margin: auto 0 auto 50px;
      }
      @media ${mediaSmMax} {
        margin: 0px 20px;
        padding-top: 35px;
        padding-bottom: 100px;
        border-bottom: 1px solid #dbdfe5;
      }

      h3 {
        font-weight: bold;
        width: 350px;
        padding-bottom: 10px;
        @media ${mediaSmMax} {
          padding-bottom: 0;
        }
        font-size: 32px;
        line-height: 44px;
        color: #0a252f;
        @media ${mediaLgMax} {
          font-size: 24px;
          width: unset;
          line-height: 32px;
        }
      }
      p {
        font-size: 16px;
        line-height: 24px;
        color: #0a252f;
        padding-bottom: 18px;
      }
      .links {
        a {
          color: #0e7873;
          text-decoration-line: underline;
          font-weight: bold;
          font-size: 16px;
          line-height: 22px;
        }
      }
    }
  }

  .faq {
    margin-top: 150px;
    margin-bottom: 100px;

    @media ${mediaMdMax} {
      margin-top: 120px;
      margin-bottom: 70px;
    }

    h3 {
      font-weight: bold;
      font-size: 32px;
      line-height: 44px;
      color: #0a252f;
      padding-bottom: 30px;
      @media ${mediaLgMax} {
        font-size: 32px;
        line-height: 44px;
      }
      @media ${mediaSmMax} {
        font-size: 24px;
        line-height: 24px;
      }
    }

    .container {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;

      .halfContainer {
        width: 47.5%;
        @media ${mediaLgMax} {
          width: 100%;
        }
      }

      .questionContainer {
        -webkit-tap-highlight-color: transparent;

        height: fit-content;
        @media ${mediaLgMax} {
          width: 100%;
        }

        &:nth-child(1) {
          border-top: 1px solid #dbdfe5;
          @media ${mediaLgMax} {
            border-top: unset !important;
          }
        }

        border-bottom: 1px solid #dbdfe5;

        &.active {
          .answear {
            transition: all 500ms;
            opacity: 1;
            height: auto;
            max-height: 500px;
            padding-bottom: 20px;
            //display: block;
          }
          .arrow {
            transition: all 0.5s;
            transform: rotate(180deg);
            height: 22px;
          }
        }

        .answear {
          transition: max-height 500ms;
          opacity: 0;
          pointer-events: none;

          max-height: 0;
          height: 0;
          //display: none;
        }

        .question {
          margin: 25px 0;
          display: flex;
          cursor: pointer;
          height: 44px;

          outline: none;

          .questionHeading {
            font-weight: 600;
            height: fit-content;
            margin: auto 0;

            font-size: 16px;
            line-height: 22px;
            padding-right: 20px;
            color: #0a252f;
          }

          .arrow {
            transition: all 500ms;
            transform-origin: center;
            margin: auto 0 auto auto;
            height: 28px;
            min-height: 22px;
            width: 28px;
          }
        }
      }
    }
  }

  .stickyBar {
    border-radius: 10px 10px 0px 0px;

    padding: 12.5px 0;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
    background: #ffffff;
    position: fixed;
    width: calc(100vw);
    bottom: 0;
    z-index: 30;

    .cap {
      display: flex;
      a {
      }

      button {
        padding: 0 20px;
        margin-left: auto;
      }

      div {
        color: #0a252f;
        margin: auto 0;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        @media ${mediaMdMax} {
          font-size: 14px;
          line-height: 22px;
        }

        @media ${mediaSmMax} {
          width: 130px;
        }
      }
    }
  }

  .mobileApp {
    margin-top: 195px;
    // Remove if we rollback the spotlight section changes
    margin-bottom: 200px;
    @media ${mediaMdMax} {
      margin-top: 100px;
    }
    background: linear-gradient(149.73deg, #063735 -2.87%, #0e7873 59.26%, #80ac66 109.13%);
    .cap {
      display: flex;
      @media ${mediaSmMax} {
        flex-flow: column-reverse;
      }
    }

    .svg {
      position: absolute;
      @media ${mediaLgMax} {
        transform: translateX(-40%);
      }
      @media ${mediaSmMax} {
        display: none;
      }
    }

    .pictureWrapper {
      position: absolute;
      @media ${mediaSmMax} {
        position: unset;
        margin: auto;
      }
    }

    .phoneImage {
      position: absolute;
      transform: translate3d(125%, -10%, 0);
      height: 500px;
      @media ${mediaLgMax} {
        transform: translate3d(80%, -10%, 0);
      }
      @media ${mediaMdMax} {
        height: 450px;
      }
      @media ${mediaSmMax} {
        position: unset;
        margin: auto;
        width: 300px;
        height: 300px;
        transform: translate3d(0, -10%, 0);
      }
    }

    .text {
      color: #ffffff;
      margin: 50px 0 60px 0;
      max-width: 380px;
      @media ${mediaLgMax} {
        max-width: 300px;
      }
      @media ${mediaSmMax} {
        margin: 0px 0 30px 0;
        max-width: 320px;
      }

      h3 {
        font-weight: bold;
        padding-bottom: 10px;
        font-size: 32px;
        line-height: 40px;
        @media ${mediaLgMax} {
          font-size: 24px;
          line-height: 32px;
        }
        @media ${mediaSmMax} {
          font-size: 23px;
          line-height: 30px;
        }
      }
      p {
        padding-bottom: 10px;
        font-size: 16px;
        line-height: 24px;
        @media ${mediaSmMax} {
          padding-bottom: 40px;
        }
      }

      .download {
        display: flex;
        width: 100%;
        @media ${mediaSmMax} {
          flex-flow: column;
          gap: 25px 0;
          a {
            max-width: 165px;
            margin-left: 0px !important;
          }
        }

        a:nth-child(2) {
          margin-left: 20px;
        }
      }
    }
  }

  .steps {
    background: rgba(245, 236, 228, 0.3);
    margin-top: 176px;
    @media ${mediaMdMax} {
      margin-top: 86px;
    }

    .step {
      &:nth-child(even) {
        .picture {
          @media ${mediaLgMax} {
            right: 0;
          }
        }
      }

      &:nth-child(odd) {
        flex-flow: row-reverse;

        .images {
          margin-left: unset;
          margin-right: auto;
        }
        .text {
          margin-right: 0px;
          @media ${mediaLgMax} {
            max-width: 450px;
          }
        }
      }

      display: flex;
      position: relative;

      @media ${mediaSmMax} {
        flex-flow: column !important;
      }

      .text {
        @media ${mediaLgMax} {
          max-width: 320px;
        }
        .stepHeading {
          font-weight: bold;
          font-size: 18px;
          padding-bottom: 10px;
          line-height: 24px;
          color: #17233c;
        }
        .stepText {
          font-size: 16px;
          line-height: 28px;
          color: #17233c;
          max-width: 420px;
        }
      }

      .images {
        margin-left: auto;
        width: 365px;
        height: 370px;
        @media ${mediaSmMax} {
          padding-top: 25px;
          height: 320px;
        }

        .svg {
          z-index: 0;
        }

        .picture {
          z-index: 1;
          border-radius: 16px;
          @media ${mediaLgMax} {
            width: 300px;
          }
        }

        .ui {
          z-index: 3;
          filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.04));
          //height: 190px;
          //image-rendering: crisp-edges;
          //width: 215px;
          image-rendering: crisp-edges;
          image-rendering: -webkit-optimize-contrast;
          border-radius: 40px;
        }

        &.step1 {
          .svg {
            transform: translate3d(180%, 0%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(180%, 30%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-20%, -5%, 0);
            }
          }
          .ui {
            transform: translate3d(-60%, -20%, 0) scale(0.45);
            @media ${mediaLgMax} {
              transform: translate3d(-25%, 0%, 0) scale(0.45);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-30%, -5%, 0) scale(0.4);
            }
          }
        }

        &.step2 {
          .svg {
            transform: translate3d(0%, -25%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(-50%, -25%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(90%, -5%, 0);
            }
          }

          .picture {
            transform: translate3d(10%, -10%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(0%, -20%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(0%, 0%, 0);
            }
          }

          .ui {
            transform: translate3d(10%, -20%, 0) scale(0.45);
            @media ${mediaLgMax} {
              transform: translate3d(-10%, -20%, 0) scale(0.4);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-20%, -25%, 0) scale(0.35);
            }
          }
        }

        &.step3 {
          .svg {
            transform: translate3d(-60%, 55%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(80%, 55%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-50%, 10%, 0);
            }
          }

          .picture {
            transform: translate3d(-25%, -15%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(0%, -15%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(0%, 0%, 0);
              left: 0;
            }
          }

          .ui {
            transform: translate3d(-15%, -10%, 0) scale(0.45);
            @media ${mediaLgMax} {
              transform: translate3d(-30%, 5%, 0) scale(0.45);
            }
            @media ${mediaSmMax} {
              transform: translate3d(25%, 0%, 0) scale(0.45);
              right: -15px;
            }
          }
        }

        &.step4 {
          .svg {
            transform: translate3d(90%, 10%, 0);
            @media ${mediaLgMax} {
              transform: translate3d(-50%, 10%, 0);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-50%, -5%, 0);
            }
          }

          .picture {
            transform: translate3d(0, -15%, 0);
            @media ${mediaSmMax} {
              transform: translate3d(5%, 0%, 0);
            }
          }

          .ui {
            transform: translate3d(-10%, -5%, 0) scale(0.45);
            @media ${mediaLgMax} {
              transform: translate3d(-20%, 0%, 0) scale(0.5);
            }
            @media ${mediaSmMax} {
              transform: translate3d(-30%, 5%, 0) scale(0.4);
            }
          }
        }

        img {
          position: absolute;
        }
      }
    }

    h3 {
      padding-top: 100px;
      font-weight: bold;
      font-size: 32px;
      line-height: 44px;
      padding-bottom: 60px;
      @media ${mediaMdMax} {
        font-size: 24px;
        padding: 60px 0 30px 0;
      }
    }
  }

  .insurance {
    .cap {
      padding-top: 150px;
      @media ${mediaMdMax} {
        padding-top: 70px;
      }
      display: flex;
      width: fit-content;
      @media ${mediaSmMax} {
        flex-flow: column;
        padding-bottom: 90px;
        margin-bottom: 70px;
      }
    }

    .text {
      padding-left: 50px;
      font-size: 16px;
      line-height: 24px;
      display: flex;
      flex-wrap: wrap;
      @media ${mediaSmMax} {
        padding-left: 0;
        padding-top: 10px;
      }

      a {
        margin-top: auto;
        font-weight: bold;
        color: #0e7873;
        text-decoration: underline;
      }
    }

    .title {
      min-width: 430px;
      @media ${mediaLgMax} {
        width: 250px;
        min-width: unset;
      }
      @media ${mediaSmMax} {
        width: unset;
      }
      h3 {
        font-weight: bold;
        font-size: 32px;
        line-height: 40px;
        @media ${mediaLgMax} {
          font-size: 24px;
          line-height: 33px;
        }
        @media ${mediaSmMax} {
          font-size: 24px;
          line-height: 30px;
        }
      }

      .partner {
        display: flex;
        padding-top: 10px;
        @media ${mediaLgMax} {
          flex-flow: column;
        }
        @media ${mediaSmMax} {
          display: flex;
          position: absolute;
          bottom: 0;
        }

        small {
          @media ${mediaLgMax} {
            padding-bottom: 10px;
            padding-top: 15px;
          }
          margin: auto 20px auto 0;
          color: #0a252f;
          font-size: 12px;
          line-height: 14px;
        }
      }
    }
  }

  .heading {
    background: linear-gradient(149.73deg, #063735 -2.87%, #0e7873 59.26%, #80ac66 109.13%);
    display: flex;
    height: 547px;

    @media ${mediaLgMax} {
      height: 493px;
    }

    @media ${mediaMdMax} {
      display: flex;
      flex-flow: column;
      height: unset;
    }

    .waves {
      position: absolute;
      transform: translateY(64%);
      @media ${mediaLgMax} {
        transform: translate3d(-65%, 50%, 0);
      }

      @media ${mediaMdMax} {
        display: none;
      }
    }

    .container {
      max-width: 380px;
      color: white;
      @media ${mediaLgMax} {
        max-width: 300px;
      }
      @media ${mediaMdMax} {
        max-width: unset;
      }

      h1 {
        font-weight: bold;
        font-size: 40px;
        letter-spacing: 0;
        line-height: 50px;
        @media ${mediaLgMax} {
          font-size: 30px;
          line-height: 37px;
        }
        @media ${mediaMdMax} {
          font-size: 24px;
          line-height: 30px;
          padding-top: 50px;
        }
      }

      p {
        font-size: 16px;
        line-height: 24px;
        margin-bottom: 30px;
        @media ${mediaLgMax} {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }

    button {
      padding: 0 20px;
      @media ${mediaMdMax} {
        width: 100%;
      }
    }

    .image {
      position: absolute;
      right: 0;
      height: 547px;
      border-top-left-radius: 300px;
      @media screen and (min-width: 1000px) and (max-width: 1400px) {
        right: -10%;
      }

      @media ${mediaLgMax} {
        right: -20%;
        border-top-left-radius: 200px;
        height: 493px;
      }
      @media ${mediaMdMax} {
        position: unset;
        padding-top: 50px;
        right: -40%;
        margin-bottom: -6px;
        height: auto;
        width: 100%;
      }
    }
  }

  .cap {
    max-width: 1076px;
    width: 100%;
    margin: auto;
    position: relative;

    @media ${mediaLgMax} {
      width: 100%;
      padding: 0 60px;
    }

    @media ${mediaSmMax} {
      padding: 0 20px;
      width: unset;
      margin: unset;
    }
  }
`;

const ReviewsScroll = styled.div`
  display: flex;
  gap: 20px;
  padding-top: 45px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Desktop: no scrolling needed, all 3 visible */
  @media ${mediaMdMin} {
    overflow-x: visible;
  }

  @media ${mediaSmMax} {
    padding-top: 25px;
  }
`;

const ReviewSlide = styled.div`
  flex-shrink: 0;
  scroll-snap-align: start;

  /* Mobile: ~1.1 slides visible */
  width: calc((100% - 20px) / 1.1);

  /* Desktop: 3 slides, 2 gaps of 20px */
  @media ${mediaMdMin} {
    width: calc((100% - 40px) / 3);
  }
`;

const NeedACaptainContent = () => (
  <NeedACaptainWrapper>
    <h2>Need a captain?</h2>
    <p> Leverage our expansive network of US Coast Guard-certified captains or captain your boat yourself.</p>
    <a
      target="_blank"
      href="https://support.boatsetter.com/hc/en-us/articles/200622265-Adding-a-captain"
      rel="noreferrer"
    >
      Learn more
    </a>
  </NeedACaptainWrapper>
);

const NeedACaptainWrapper = SpotlightSection.makeStyledSpotlightWrapper({
  backgroundColor: '#004e60',
  linkColor: '#72d4ba',
});

const NeedAHandContent = () => (
  <NeedAHandWrapper>
    {' '}
    <h2>Need a hand?</h2>
    <p>
      Hire a boat manager to respond to booking requests and messages, update the calendar, and edit listings on your
      behalf.
    </p>
    <a target="_blank" href="https://www.boatsetter.com/boating-resources/introducing-boat-managers/" rel="noreferrer">
      Learn more
    </a>
  </NeedAHandWrapper>
);

const NeedAHandWrapper = SpotlightSection.makeStyledSpotlightWrapper({
  backgroundColor: '#0e7873',
  linkColor: '#f4c064',
});
